import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/styles";
import Grid from "@material-ui/core/Grid";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepButton from "@material-ui/core/StepButton";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import Paper from "@material-ui/core/Paper";
import SvgIcon from "@material-ui/core/SvgIcon";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import ButtonBase from "@material-ui/core/ButtonBase";
import ExpansionPanel from "@material-ui/core/ExpansionPanel";
import ExpansionPanelSummary from "@material-ui/core/ExpansionPanelSummary";
import ExpansionPanelDetails from "@material-ui/core/ExpansionPanelDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Gallery from "react-grid-gallery";
import { Link, navigate } from "@reach/router";

import SERVER_ADDRESS from "../config";

const useStyles = makeStyles(theme => ({
  title: {
    paddingTop: theme.spacing(2)
  },
  subtitle: {
    margin: theme.spacing(1)
  },
  img: {
    maxHeight: "600px",
    maxWidth: "99%"
  },
  imageContainer: {
    position: "relative",
    display: "inline-block",
    marginBottom: "25px"
  },
  docImage: {
    maxWidth: "100%",
    display: "block"
  },
  imageTitle: {
    position: "absolute",
    top: "100%",
    left: "46%",
    "z-index": 10
  },
  docPaper: {
    padding: theme.spacing(1),
    margin: theme.spacing(2)
  },
  button: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1)
  },
  doclink: {
    color: theme.palette.primary.light,
    "text-decoration": "none"
  },
  stepContent: {
    margin: theme.spacing(2)
  },
  paragraph: {
    "text-indent": theme.spacing(4),
    "line-height": "200%",
    margin: theme.spacing(2)
  },
  panel: {
    width: "100%",
    marginBottom: theme.spacing(1)
  },
  secondaryPanelHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary
  },
  text: {
    "line-height": "200%"
  },
  tabWithContent: {
    width: "100%"
  }
}));

//衬底Paper
function DocPaper(props) {
  const classes = useStyles();

  return (
    <Paper className={classes.docPaper} {...props}>
      {props.children}
    </Paper>
  );
}

//标题
function Title(props) {
  const classes = useStyles();
  const { color } = props;

  return (
    <Typography
      variant="h5"
      color={color ? color : "secondary"}
      align="center"
      className={classes.title}
    >
      {props.children}
    </Typography>
  );
}

//小标题
function SubTitle(props) {
  const classes = useStyles();
  const { color } = props;
  return (
    <Typography
      variant="h6"
      color={color ? color : "textPrimary"}
      align="left"
      className={classes.subtitle}
    >
      {props.children}
    </Typography>
  );
}

//段落
function Paragraph(props) {
  const classes = useStyles();
  const { onMouseOver } = props;
  return (
    <Typography
      variant="body1"
      color="textSecondary"
      paragraph
      component="p"
      onClick={onMouseOver}
      className={classes.paragraph}
      style={onMouseOver ? { cursor: "pointer" } : null}
    >
      {props.children}
    </Typography>
  );
}

//图片组，接收Array作为src
function DocImages(props) {
  const classes = useStyles();
  const { imageSrcs, imageNumber, titlePostion } = props;
  return (
    <div align="center">
      {imageSrcs.map((imagesrc, index) => {
        //图片标题
        const title = `图${imageNumber}-${index + 1}`;
        return (
          <div className={classes.imageContainer} key={index}>
            <img src={imagesrc} alt="" title={title} className={classes.img} />
            <Typography
              variant="subtitle1"
              gutterBottom
              className={classes.imageTitle}
              style={titlePostion[index]}
            >
              {title}
            </Typography>
          </div>
        );
      })}
    </div>
  );
}

//单张图片，接收src作为图片地址
function DocImage(props) {
  const classes = useStyles();
  const { imageSrc } = props;
  return (
    <div align="center">
      <img src={imageSrc} alt="" className={classes.docImage} />
    </div>
  );
}

//动态图片组Hook
function useDynamicImage(initSrc) {
  const [src, setSrc] = useState(initSrc);
  return [src, setSrc];
}

//导航按钮，用于导航到其他页面，有文本button与iconbutton两种模式
function NavigateButton(props) {
  const classes = useStyles();

  const { icon, buttonText, to, backward, iconStyle, newpage } = props;
  const handleClick = () => {
    if (newpage) {
      window.open(to);
    } else {
      navigate(to);
    }
  };
  return !icon ? (
    //文本Button
    <Button
      color="primary"
      onClick={handleClick}
      variant="text"
      className={classes.button}
    >
      {buttonText}
    </Button>
  ) : (
    //IconButton图标，默认前进箭头,backward属性退后箭头
    <IconButton
      title={buttonText}
      onClick={handleClick}
      color="primary"
      className={classes.button}
      style={iconStyle}
    >
      <SvgIcon>
        {backward ? (
          <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
        ) : (
          <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
        )}
      </SvgIcon>
    </IconButton>
  );
}

//文本按钮
function TextButton(props) {
  const classes = useStyles();

  const { color, onClick } = props;

  return (
    <Button
      color={color ? color : "primary"}
      onClick={onClick}
      variant="text"
      className={classes.button}
    >
      {props.children}
    </Button>
  );
}

//段落中使用的链接
function DocLink(props) {
  const classes = useStyles();
  const { to, newpage } = props;
  return (
    <>
      {newpage ? (
        <Link
          to={to}
          className={classes.doclink}
          onClick={e => {
            e.preventDefault();
            window.open(to);
          }}
        >
          {props.children}
        </Link>
      ) : (
        <Link to={to} className={classes.doclink}>
          {props.children}
        </Link>
      )}
    </>
  );
}

//可展开的内容面板
function Panel(props) {
  const classes = useStyles();
  const { panelTitle, panelDetail, defaultOpen } = props;
  return (
    <div className={classes.panel}>
      <ExpansionPanel defaultExpanded={defaultOpen}>
        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
          {panelTitle}
        </ExpansionPanelSummary>
        <ExpansionPanelDetails style={{ display: "block" }}>
          {panelDetail}
        </ExpansionPanelDetails>
      </ExpansionPanel>
    </div>
  );
}

//定制化可点击文本
function Text(props) {
  const classes = useStyles();
  const { color, newpage, to } = props;

  const handleClick = () => {
    if (newpage) {
      window.open(to);
    } else {
      navigate(to);
    }
  };
  return (
    <Typography
      variant="body1"
      color={color ? color : "textPrimary"}
      component="span"
      className={classes.text}
      onClick={to ? handleClick : null}
    >
      {props.children}
    </Typography>
  );
}

function TabWithContent(props) {
  const classes = useStyles();
  const { content } = props;
  const [value, setValue] = React.useState(0);
  function handleChange(event, newValue) {
    setValue(newValue);
  }
  return (
    <div
      style={{
        display: "block"
      }}
    >
      <Tabs
        value={value}
        onChange={handleChange}
        indicatorColor="secondary"
        textColor="secondary"
        variant="fullWidth"
        className={classes.tabWithContent}
      >
        {content.map(({ title }, index) => (
          <Tab label={title} key={index} />
        ))}
      </Tabs>
      <div
        style={{
          display: "block"
        }}
      >
        {content[value].detail}
      </div>
    </div>
  );
}

function SimpleGallery(props) {
  const { images, thumbHeight, thumbWidth, rowHeight } = props;

  const galleryImages = images.map(imageNow => {
    const { thumbnail, image } = imageNow;

    return {
      src: image,
      thumbnail: thumbnail,
      thumbnailWidth: thumbWidth,
      thumbnailHeight: thumbHeight,
      caption: "点击查看大图"
    };
  });
  return (
    <div
      style={{
        display: "block",
        minHeight: "1px",
        width: "100%",
        border: "1px solid #ddd",
        overflowY: "auto",
        overflowX: "hidden"
      }}
    >
      <Gallery
        style={{
          display: "block",
          width: "100%"
        }}
        images={galleryImages}
        enableImageSelection={false}
        imageCountSeparator={"/"}
        showLightboxThumbnails={true}
        rowHeight={rowHeight ? rowHeight : 135}
        backdropClosesModal
        lightboxWidth={1920}
        onClickImage={e => {
          window.open(e.target.src);
        }}
      />
    </div>
  );
}
export {
  Title,
  Paragraph,
  SubTitle,
  DocImage,
  DocImages,
  NavigateButton,
  DocPaper,
  DocLink,
  Panel,
  Text,
  TextButton,
  TabWithContent,
  useDynamicImage,
  SimpleGallery
};
